export type FacilityOperatingHours = {
  dayOfWeek: number;
  /** `HH:mm:ss` in form state. `null` on the wire when the time is not set. */
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
};

type OperatingHoursInput = {
  dayOfWeek?: number | null;
  openTime?: string | null;
  closeTime?: string | null;
  isClosed?: boolean | null;
  key?: unknown;
};

const CLOCK_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?/;

/**
 * API operating times are TimeSpan values (`HH:mm` or `HH:mm:ss`).
 * TimePicker emits `YYYY-MM-DDTHH:mm`, which the day validator rejects.
 */
export const toOperatingTimeSpan = (
  value: string | null | undefined,
): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const clock = trimmed.includes('T')
    ? trimmed.slice(trimmed.indexOf('T') + 1)
    : trimmed;
  const match = CLOCK_TIME.exec(clock);
  if (match == null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || '0');
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/** Value TimePicker can display and edit without writing a date back into the model. */
export const operatingTimeForPicker = (
  value: string | null | undefined,
): string => {
  const span = toOperatingTimeSpan(value);
  return span == null ? '' : `2000-01-01T${span}`;
};

/** Stores a TimePicker change as `HH:mm:ss`, or `''` when the input is cleared. */
export const operatingTimeFromPicker = (value: string): string =>
  toOperatingTimeSpan(value) ?? '';

const editorDay = (
  dayOfWeek: number,
  day: OperatingHoursInput | undefined,
): FacilityOperatingHours => {
  const openTime = toOperatingTimeSpan(day?.openTime) ?? '';
  const closeTime = toOperatingTimeSpan(day?.closeTime) ?? '';
  const noTimes = openTime.length === 0 && closeTime.length === 0;
  // Saved "no hours" and an explicit Closed day are both null times, so both load as open and blank.
  return {
    dayOfWeek,
    openTime,
    closeTime,
    isClosed: noTimes ? false : Boolean(day?.isClosed),
  };
};

/** Fills Monday–Sunday. Days with no times stay open with blank clocks. */
export const parseOperatingHours = (
  hours: readonly OperatingHoursInput[] | null | undefined,
): FacilityOperatingHours[] => {
  const byDay = new Map<number, OperatingHoursInput>();
  for (const day of hours ?? []) {
    const dayOfWeek = day.dayOfWeek;
    if (dayOfWeek == null || dayOfWeek < 1 || dayOfWeek > 7) continue;
    byDay.set(dayOfWeek, day);
  }
  return Array.from({ length: 7 }, (_, index) =>
    editorDay(index + 1, byDay.get(index + 1)),
  );
};

/**
 * Always one row per weekday. Times are `HH:mm:ss`.
 * A day with no times is closed and sends null times. The editor-only `key` is dropped.
 */
export const serializeOperatingHours = (
  hours: readonly OperatingHoursInput[] | null | undefined,
): FacilityOperatingHours[] => {
  const byDay = new Map<number, OperatingHoursInput>();
  for (const day of hours ?? []) {
    const dayOfWeek = day.dayOfWeek;
    if (dayOfWeek == null || dayOfWeek < 1 || dayOfWeek > 7) continue;
    byDay.set(dayOfWeek, day);
  }
  return Array.from({ length: 7 }, (_, index) => {
    const dayOfWeek = index + 1;
    const day = byDay.get(dayOfWeek);
    const openTime = toOperatingTimeSpan(day?.openTime);
    const closeTime = toOperatingTimeSpan(day?.closeTime);
    const isClosed =
      (openTime == null && closeTime == null) || Boolean(day?.isClosed);
    return {
      dayOfWeek,
      openTime: isClosed ? null : openTime,
      closeTime: isClosed ? null : closeTime,
      isClosed,
    };
  });
};

export const normalizeFacilityDetails = <T extends FacilityHoursValues>(
  details: T,
): T => ({
  ...details,
  pickupHours: parseOperatingHours(details.pickupHours),
  receivingHours: parseOperatingHours(details.receivingHours),
});

export const serializeFacilityDetails = <T extends FacilityHoursValues>(
  details: T,
): T => ({
  ...details,
  pickupHours: serializeOperatingHours(details.pickupHours),
  receivingHours: serializeOperatingHours(details.receivingHours),
});

export const mapStopFacilityDetails = <
  TOrder extends {
    loads?: Array<{
      stops?: Array<{ facilityDetails?: FacilityHoursValues | null }> | null;
    }> | null;
  },
>(
  order: TOrder,
  mapDetails: <T extends FacilityHoursValues>(details: T) => T,
): TOrder => {
  if (!order.loads) return order;
  return {
    ...order,
    loads: order.loads.map((load) => {
      if (!load.stops) return load;
      return {
        ...load,
        stops: load.stops.map((stop) => {
          if (!stop.facilityDetails) return stop;
          return {
            ...stop,
            facilityDetails: mapDetails(stop.facilityDetails),
          };
        }),
      };
    }),
  };
};

export const MAX_PORTAL_CREDENTIALS_LENGTH = 500;

/** Portal URL and login info only exist for this scheduling method. */
export const PORTAL_SCHEDULING_METHOD = 'Portal';

/** Computed by the API, never sent back. */
export type FacilityLinkStatus =
  | 'Manual'
  | 'Linked'
  | 'Overridden'
  | 'Detached';

type FacilityValues = {
  appointmentType: string;
  schedulingMethod: string;
  schedulingMethodDetails: string;
  portalUrl: string;
  /** Decrypted JSON credential record. Stored encrypted server-side. */
  portalCredentials: string;
  pickupHoursNotes: string;
  receivingHoursNotes: string;
  receivingUsesPickupHours: boolean;
  pickupHours: FacilityOperatingHours[];
  receivingHours: FacilityOperatingHours[];
};

/** The facility record on an address book entry. Many stops may link to it. */
export type FacilityDetails = FacilityValues & {
  id: number;
  version: number;
};

/** A stop's own values. Always explicit; never resolved from the facility at read time. */
export type StopFacilityDetails = FacilityValues & {
  facilityDetailsId: number | null;
  facilityDetailsVersion: number | null;
  isOverridden: boolean;
  linkStatus: FacilityLinkStatus;
  /** The linked facility has been saved since these values were copied. */
  facilityHasChanges: boolean;
};

/** The slice of facility values the weekly hours editor owns. */
export type FacilityHoursValues = Pick<
  FacilityValues,
  | 'pickupHours'
  | 'receivingHours'
  | 'pickupHoursNotes'
  | 'receivingHoursNotes'
  | 'receivingUsesPickupHours'
>;

const createWeek = (): FacilityOperatingHours[] =>
  Array.from({ length: 7 }, (_, index) => ({
    dayOfWeek: index + 1,
    openTime: '',
    closeTime: '',
    isClosed: false,
  }));

const createValues = (): FacilityValues => ({
  appointmentType: '',
  schedulingMethod: '',
  schedulingMethodDetails: '',
  portalUrl: '',
  portalCredentials: '',
  pickupHoursNotes: '',
  receivingHoursNotes: '',
  receivingUsesPickupHours: false,
  pickupHours: createWeek(),
  receivingHours: createWeek(),
});

export const createFacilityDetails = (): FacilityDetails => ({
  ...createValues(),
  id: 0,
  version: 0,
});

export const createStopFacilityDetails = (): StopFacilityDetails => ({
  ...createValues(),
  facilityDetailsId: null,
  facilityDetailsVersion: null,
  isOverridden: false,
  linkStatus: 'Manual',
  facilityHasChanges: false,
});

/** Copies a facility's current values onto a stop and links the two. */
export const copyFacilityToStop = (
  facility: FacilityDetails,
): StopFacilityDetails => ({
  appointmentType: facility.appointmentType,
  schedulingMethod: facility.schedulingMethod,
  schedulingMethodDetails: facility.schedulingMethodDetails,
  portalUrl: facility.portalUrl,
  portalCredentials: facility.portalCredentials,
  pickupHoursNotes: facility.pickupHoursNotes,
  receivingHoursNotes: facility.receivingHoursNotes,
  receivingUsesPickupHours: facility.receivingUsesPickupHours,
  pickupHours: parseOperatingHours(facility.pickupHours),
  receivingHours: parseOperatingHours(facility.receivingHours),
  facilityDetailsId: facility.id,
  facilityDetailsVersion: facility.version,
  isOverridden: false,
  linkStatus: 'Linked',
  facilityHasChanges: false,
});

/** Promotes a stop's values into a new address book facility record. */
export const stopFacilityToFacility = (
  stop: StopFacilityDetails,
): FacilityDetails => ({
  appointmentType: stop.appointmentType,
  schedulingMethod: stop.schedulingMethod,
  schedulingMethodDetails: stop.schedulingMethodDetails,
  portalUrl: stop.portalUrl,
  portalCredentials: stop.portalCredentials,
  pickupHoursNotes: stop.pickupHoursNotes,
  receivingHoursNotes: stop.receivingHoursNotes,
  receivingUsesPickupHours: stop.receivingUsesPickupHours,
  pickupHours: parseOperatingHours(stop.pickupHours),
  receivingHours: parseOperatingHours(stop.receivingHours),
  id: 0,
  version: 0,
});

/** Deep-copies hours so a copied stop does not share arrays with the source. */
export const cloneStopFacilityDetails = (
  facilityDetails?: StopFacilityDetails | null,
): StopFacilityDetails | undefined => {
  if (facilityDetails == null) return undefined;
  return {
    ...facilityDetails,
    pickupHours: parseOperatingHours(facilityDetails.pickupHours),
    receivingHours: parseOperatingHours(facilityDetails.receivingHours),
  };
};

export const facilityLinkLabel = (details: StopFacilityDetails): string => {
  switch (details.linkStatus) {
    case 'Linked':
      return details.facilityHasChanges
        ? 'Linked to facility - facility has been updated'
        : 'Linked to facility';
    case 'Overridden':
      return 'Overridden for this stop';
    case 'Detached':
      return 'Facility no longer exists - using stop values';
    default:
      return 'Entered for this stop';
  }
};
